/**
 * Types for the tile companion JSON file (e.g. chunk_1.json alongside chunk_1.glb).
 * Schema version: tile_v3
 */

export type TileJsonMarker = {
  id: string;
  name: string;
  attribute: string | null;
  model: string;
  label: string;
  size: number;
  colour: string | null;
  health: number | null;
  yaw: number;
  offset_x: number;
  offset_y: number;
  offset_z: number;
  resourceType: string | null;
  resourceValue: number | null;
  /** Rarity bucket: 'common' | 'uncommon' | 'rare' | null */
  rarity?: string | null;
  /** Interactable object type e.g. 'datacache' | 'beacon' | 'cache' | null */
  object_type?: string | null;
};

export type TileJsonNode = {
  id: string;
  kind: 'node' | 'gateway';
  name: string;
  /** Shipwreck name, present on kind === 'node' only */
  node_name?: string;
  /** Three.js world-space position */
  x: number;
  y: number;
  z: number;
  radius_xz: number;
  radius_y: number;
  /**
   * Footprint of the chamber (optional; absent on hand-made tiles = 'round', an ellipse of radius_xz x radius_y).
   *  - 'round': ellipsoid (cave chamber).
   *  - 'box':   rectangular chamber with half extents half_x / half_y / half_z (tile-local axes) rotated by `yaw`.
   *  - 'tube':  a pipe / shaft: circular in plan (radius_xz) and half_y tall (a vertical shaft).
   */
  shape?: 'round' | 'box' | 'tube';
  half_x?: number;
  half_y?: number;
  half_z?: number;
  /** Rotation of a box about the vertical axis, degrees (0 = axes aligned with the tile). */
  yaw?: number;
  attributes: string[];
  decorations: Record<string, unknown>;
  markers: TileJsonMarker[];
  /** Authoring size category — do not use for display */
  size_category?: string;
  y_category?: string;
  life?: string | null;
  visibility?: string;
  communications?: string | null;
  /** Gateway-only: which face this gateway sits on */
  side?: string;
  /** Gateway-only: whether the passage is open */
  open?: boolean;
};

export type TileJsonConnection = {
  id: string;
  /** Source node id (tile-local) */
  from: string;
  /** Target node id (tile-local) */
  to: string;
  size_category: string;
  /** Passage width (diameter for a round tube; full width of a box corridor). */
  width: number;
  /** Cross-section: 'round' tube or 'box' corridor. Absent on hand-made tiles (round). */
  shape?: 'round' | 'box';
  bend: string;
  /** Intermediate waypoints in Three.js world space [x, y, z] */
  waypoints: [number, number, number][];
  decorations: Record<string, unknown>;
};

export type TileJson = {
  schema: string;
  schemaVersion: number;
  metadata: {
    tile_id: string;
    biome: string;
    chunk_region: string;
    temp_c: number;
    grid_x: number | null;
    grid_y: number | null;
    grid_z: number | null;
    rotation_degrees: number;
    author: string;
    created: string;
    seed: string;
  };
  chunk: {
    size_xz: number;
    size_y: number;
    gateway_y: number;
    cave_mode: boolean;
  };
  nodes: TileJsonNode[];
  connections: TileJsonConnection[];
  generation?: { crossings: number; timestamp: string; /** Generator tile kind, e.g. 'open_seabed' or 'cave_maze' (absent on hand-made tiles = open water). */ kind?: string };
};

/**
 * A connection that has been resolved to two node endpoints, potentially
 * spanning multiple tiles via gateway stitching.
 */
export type AggregatedConnection = {
  id: string;
  /** "tileId:nodeId" */
  fromKey: string;
  /**
   * "tileId:nodeId" for a real destination node.
   * "tileId:gatewayId" when deadEnd is true (gateway has no matching cross-tile partner).
   */
  toKey: string;
  width: number;
  /** Cross-section of the passage (round tube or box corridor); absent on hand-made tiles (treated as round). */
  shape?: 'round' | 'box';
  /** Full world-space polyline including waypoints from both tiles if cross-tile */
  waypoints: [number, number, number][];
  /**
   * True when the connection leads to a gateway that has no matching partner on
   * an adjacent tile (a sealed passage / dead end). The gateway's world-space
   * position is appended as the final entry in `waypoints`.
   */
  deadEnd?: true;
};

/** Gateway faces that meet: the +x face of one tile is the -x face of its neighbour. */
const OPPOSITE_SIDE: Record<string, string> = { '+x': '-x', '-x': '+x', '+z': '-z', '-z': '+z', '+y': '-y', '-y': '+y' };
/** Two gateways are the same opening when their world positions differ by at most this much along the face normal (each sits GATEWAY_INSET inside its own face) ... */
const GATEWAY_NORMAL_TOLERANCE = 6;
/** ... and by at most this much across it. */
const GATEWAY_LATERAL_TOLERANCE = 2.5;

type Offset = { x: number; z: number; y?: number };
type GatewayRef = { tileId: string; node: TileJsonNode; wx: number; wy: number; wz: number };
type Pt = [number, number, number];

/** True when gateway `a` and gateway `b` (different tiles) are the two halves of one opening. */
const gatewaysMeet = (a: GatewayRef, b: GatewayRef): boolean => {
  if (a.tileId === b.tileId) return false;
  if (a.node.side && b.node.side && OPPOSITE_SIDE[a.node.side] !== b.node.side) return false;
  const side = a.node.side ?? b.node.side ?? '';
  const dx = Math.abs(a.wx - b.wx), dy = Math.abs(a.wy - b.wy), dz = Math.abs(a.wz - b.wz);
  const normal = side[1] === 'x' ? dx : side[1] === 'y' ? dy : side[1] === 'z' ? dz : Math.min(dx, dy, dz);
  const lateral = side[1] === 'x' ? Math.max(dy, dz) : side[1] === 'y' ? Math.max(dx, dz) : side[1] === 'z' ? Math.max(dx, dy) : Math.max(dx, dy, dz);
  return normal <= GATEWAY_NORMAL_TOLERANCE && lateral <= GATEWAY_LATERAL_TOLERANCE;
};

/** A connection of one tile oriented node -> gateway (or node -> node), waypoints in world space ordered from the node. */
type Oriented = { tileId: string; conn: TileJsonConnection; nodeId: string; endId: string; endIsGateway: boolean; points: Pt[] };

/**
 * Aggregate connections across multiple tiles into node-to-node connections.
 * Connections that end at a gateway are stitched together through the gateway of the neighbouring tile that meets it
 * (opposite face, same opening within GATEWAY_*_TOLERANCE: a generated tile's gateway node sits GATEWAY_INSET inside its face, so the
 * two halves of an opening are ~4 m apart in world space).
 *
 * Connections may be stored in either direction: generated tiles write gateway -> node, hand-made tiles node -> gateway. Both are
 * normalised so the real node is the start. (An earlier version only handled node -> gateway, so every passage of a generated tile
 * that ran from a gateway was silently dropped.)
 *
 * @param tileJsons    Map of tile_id → TileJson
 * @param tileOffsets  Map of tile_id → world-space origin offset for that tile. `y` is the tile layer's vertical
 *                     offset (stacked tiles, see getTileVerticalOffset); it shifts waypoint Y and keeps vertical
 *                     (+y / -y) gateways from matching anything but the face of the tile above/below.
 */
export function aggregateTileConnections(
  tileJsons: Map<string, TileJson>,
  tileOffsets: Map<string, Offset>
): AggregatedConnection[] {
  const nodeByKey = new Map<string, TileJsonNode>();
  const gateways: GatewayRef[] = [];
  const gatewayByKey = new Map<string, GatewayRef>();
  for (const [tileId, tj] of tileJsons) {
    const off = tileOffsets.get(tileId) ?? { x: 0, z: 0 };
    for (const node of tj.nodes) {
      nodeByKey.set(`${tileId}:${node.id}`, node);
      if (node.kind !== 'gateway') continue;
      const ref: GatewayRef = { tileId, node, wx: node.x + off.x, wy: node.y + (off.y ?? 0), wz: node.z + off.z };
      gateways.push(ref);
      gatewayByKey.set(`${tileId}:${node.id}`, ref);
    }
  }

  const partnerOf = (gw: GatewayRef): GatewayRef | null => gateways.find((other) => gatewaysMeet(gw, other)) ?? null;

  const orient = (tileId: string, conn: TileJsonConnection): Oriented | null => {
    const off = tileOffsets.get(tileId) ?? { x: 0, z: 0 };
    const a = nodeByKey.get(`${tileId}:${conn.from}`);
    const b = nodeByKey.get(`${tileId}:${conn.to}`);
    if (!a || !b) return null;
    const pts = applyOffset(conn.waypoints, off);
    if (a.kind === 'node' && b.kind === 'node') return { tileId, conn, nodeId: conn.from, endId: conn.to, endIsGateway: false, points: pts };
    if (a.kind === 'node' && b.kind === 'gateway') return { tileId, conn, nodeId: conn.from, endId: conn.to, endIsGateway: true, points: pts };
    if (a.kind === 'gateway' && b.kind === 'node') return { tileId, conn, nodeId: conn.to, endId: conn.from, endIsGateway: true, points: pts.slice().reverse() };
    return null; // gateway -> gateway
  };

  const orientedByGateway = new Map<string, Oriented[]>();
  const all: Oriented[] = [];
  for (const [tileId, tj] of tileJsons) {
    for (const conn of tj.connections) {
      const o = orient(tileId, conn);
      if (!o) continue;
      all.push(o);
      if (o.endIsGateway) {
        const k = `${tileId}:${o.endId}`;
        if (!orientedByGateway.has(k)) orientedByGateway.set(k, []);
        orientedByGateway.get(k)!.push(o);
      }
    }
  }

  const result: AggregatedConnection[] = [];
  const seen = new Set<string>();
  const push = (c: AggregatedConnection): void => {
    // A stitched passage is found from both ends; keep one (order-independent key).
    const key = `${[c.fromKey, c.toKey].sort().join('|')}|${c.waypoints.length}`;
    if (seen.has(key)) return;
    seen.add(key);
    result.push(c);
  };

  for (const o of all) {
    const connId = `${o.tileId}:${o.conn.id}`;
    const fromKey = `${o.tileId}:${o.nodeId}`;
    const shape = o.conn.shape;
    if (!o.endIsGateway) {
      push({ id: connId, fromKey, toKey: `${o.tileId}:${o.endId}`, width: o.conn.width, ...(shape ? { shape } : {}), waypoints: o.points });
      continue;
    }
    const gw = gatewayByKey.get(`${o.tileId}:${o.endId}`)!;
    const gwPoint: Pt = [gw.wx, gw.wy, gw.wz];
    const partner = partnerOf(gw);
    const others = partner ? (orientedByGateway.get(`${partner.tileId}:${partner.node.id}`) ?? []) : [];
    if (!partner || others.length === 0) {
      // Sealed gateway / dead end (or the neighbour is not loaded yet): the renderer shows a stub up to the gateway.
      push({ id: connId, fromKey, toKey: `${o.tileId}:${o.endId}`, width: o.conn.width, ...(shape ? { shape } : {}), waypoints: [...o.points, gwPoint], deadEnd: true });
      continue;
    }
    const partnerPoint: Pt = [partner.wx, partner.wy, partner.wz];
    for (const other of others) {
      const chained: Pt[] = [...o.points, gwPoint, partnerPoint, ...other.points.slice().reverse()];
      const chainShape = shape ?? other.conn.shape;
      push({
        id: `${connId}+${other.tileId}:${other.conn.id}`,
        fromKey,
        toKey: `${other.tileId}:${other.nodeId}`,
        width: Math.min(o.conn.width, other.conn.width),
        ...(chainShape ? { shape: chainShape } : {}),
        waypoints: chained
      });
    }
  }
  return result;
}

function applyOffset(waypoints: Pt[], offset: Offset): Pt[] {
  const oy = offset.y ?? 0;
  return waypoints.map(([x, y, z]) => [x + offset.x, y + oy, z + offset.z]);
}
